import React, { useState, useEffect } from 'react';
import { FiAlertTriangle, FiCheckCircle, FiInfo, FiTrendingUp, FiTrendingDown, FiRefreshCw, FiPlay, FiLoader, FiBarChart2 } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { versionPourSauvegarde } from '../../utils/resumeTurpe';

// Composants UI compacts pour 13"
const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-lg border border-gray-200 shadow-sm ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children, className = "" }) => (
  <div className={`px-2 py-1.5 border-b border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardTitle = ({ children, className = "" }) => (
  <h3 className={`text-xs font-semibold text-gray-900 ${className}`}>
    {children}
  </h3>
);

const CardContent = ({ children, className = "" }) => (
  <div className={`p-2 ${className}`}>
    {children}
  </div>
);

const Button = ({ children, className = "", variant = "default", size = "default", ...props }) => {
  const baseClasses = "inline-flex items-center justify-center rounded font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";
  
  const variants = {
    default: "bg-blue-600 text-white hover:bg-blue-700",
    outline: "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50",
    ghost: "text-gray-700 hover:bg-gray-100"
  };

  const sizes = {
    sm: "px-2 py-1 text-xs",
    default: "px-1.5 py-2 text-xs",
    lg: "px-2 py-2 text-xs"
  };

  return (
    <button 
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

const Badge = ({ children, className = "" }) => (
  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${className}`}>
    {children}
  </span>
);

const Alert = ({ children, className = "", variant = "default" }) => {
  const variants = {
    default: "border-gray-200 bg-gray-50",
    destructive: "border-red-200 bg-red-50"
  };

  return (
    <div className={`rounded border p-1.5 ${variants[variant]} ${className}`}>
      {children}
    </div>
  );
};

const AlertDescription = ({ children, className = "" }) => (
  <div className={`text-xs ${className}`}>
    {children}
  </div>
);

// Composant principal
const VersionUtilisationOptimizer = ({
  pdlData,
  session,
  pdlId,
  analysisYear,
  chosenVersionOfUse,
  onAnalysisComplete,
  onChosenVersionChange,
  savedAnalysis,
}) => {
  // savedAnalysis = result_data persisté en BDD (version_of_use_details_json).
  // On l'utilise comme état initial pour restaurer l'affichage au retour sur
  // la page sans re-lancer l'API. Sa structure est celle retournée par
  // /api/v4/analyze-version-utilisation (financial_analysis, recommendation, ...).
  const [analysis, setAnalysis] = useState(savedAnalysis || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);

  // Proceed = poser chosen_version_of_use = recommended_version via set_chosen.
  // Affiche "Version validée optimisée" après succès.
  const handleConfirmOptimization = async () => {
    if (!pdlId || !session?.access_token) {
      toast.error('Données manquantes pour valider l\'optimisation.');
      return;
    }
    const recommended = analysis?.recommended_version;
    if (!recommended) {
      toast.error('Pas de version recommandée à valider.');
      return;
    }
    setConfirming(true);
    try {
      const res = await fetch('/api/turpe/update-analysis-result', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          pdl_id: pdlId,
          analysis_type: 'set_chosen',
          analysis_year: analysisYear,
          result_data: { chosen_version_of_use: recommended },
        }),
      });
      if (!res.ok) throw new Error('save failed');
      toast.success(`Version ${recommended} validée pour l'optimisation.`);
      if (onChosenVersionChange) onChosenVersionChange(recommended);
    } catch (e) {
      console.error('confirm optim error:', e);
      toast.error('Échec de la validation de l\'optimisation.');
    } finally {
      setConfirming(false);
    }
  };

  // Sync si savedAnalysis arrive en async après le mount
  useEffect(() => {
    if (savedAnalysis && !analysis) setAnalysis(savedAnalysis);
  }, [savedAnalysis]); // eslint-disable-line react-hooks/exhaustive-deps

  const analyzeVersionUtilisation = async () => {
    if (!pdlData || !session) {
      setError('Données PDL ou session manquantes');
      return;
    }

    setLoading(true);
    setError(null);
    setAnalysis(null);
    
    try {
      const formData = new FormData();
      formData.append('pdl_number', pdlData.pdl_number);
      // Fallback session.user.id pour les PDLs SFTP sans pdl.user_id legacy.
      formData.append('user_id', pdlData.user_id || session?.user?.id || '');
      formData.append('analysis_year', new Date().getFullYear() - 1);

      const response = await fetch('/api/v4/analyze-version-utilisation', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        },
        body: formData
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        setAnalysis(result);
        toast.success("Analyse CU / MU / LU terminée !");

        // On expose au parent toutes les données nécessaires pour la modale
        // "Choix final" : les 3 coûts par version, version actuelle, recommandée.
        const adaptedResult = versionPourSauvegarde(result);

        if (onAnalysisComplete) {
          // On envoie bien l'objet adapté, c'est parfait !
          onAnalysisComplete(adaptedResult);
        }

      } else {
        const errorMessage = result.detail || result.error || 'Erreur lors de l\'analyse';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error('Erreur analyse version utilisation:', err);
      const errorMessage = 'Erreur de connexion au serveur';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount || isNaN(amount)) return '0 €';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getRecommendationIcon = (recommendation) => {
    switch (recommendation) {
      case 'RECOMMANDÉ': return <FiCheckCircle className="h-3 w-3 text-green-500" />;
      case 'NON RECOMMANDÉ': return <FiAlertTriangle className="h-3 w-3 text-red-500" />;
      default: return <FiInfo className="h-3 w-3 text-blue-500" />;
    }
  };

  const getRecommendationBg = (recommendation) => {
    switch (recommendation) {
      case 'RECOMMANDÉ': return 'border-green-200 bg-green-50';
      case 'NON RECOMMANDÉ': return 'border-red-200 bg-red-50';
      default: return 'border-blue-200 bg-blue-50';
    }
  };

  return (
    <div className="space-y-2 p-2">
      {/* Header compact */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-2 rounded-lg border border-purple-200">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-bold text-gray-900 flex items-center">
            {analysis && !analysis.already_optimized ? 
              getRecommendationIcon(analysis.recommendation) : 
              <FiBarChart2 className="h-3.5 w-3.5 text-purple-600" />
            }
            <span className="ml-2">Version d'Utilisation</span>
          </h3>
          {analysis && (
            <Badge className="bg-purple-100 text-purple-700 border border-purple-200">
              Analysé
            </Badge>
          )}
        </div>
        <p className="text-gray-600 text-xs">
          Analyse comparative CU / MU / LU pour optimiser votre contrat
        </p>
      </div>

      {/* Contenu principal */}
      <Card>
        <CardContent>
          {loading ? (
            // État chargement - compact
            <div className="flex items-center justify-center space-x-3 py-6">
              <FiLoader className="h-3.5 w-3.5 animate-spin text-purple-600" />
              <div>
                <p className="font-medium text-gray-900 text-xs">Analyse en cours...</p>
                <p className="text-xs text-gray-500">Comparaison CU / MU / LU</p>
              </div>
            </div>

          ) : error ? (
            // État erreur - compact
            <div className="text-center py-1.5">
              <Alert variant="destructive">
                <FiAlertTriangle className="h-3 w-3" />
                <AlertDescription>
                  <strong>Erreur :</strong> {error}
                </AlertDescription>
              </Alert>
              <Button 
                onClick={analyzeVersionUtilisation} 
                className="mt-3"
                variant="outline"
                size="sm"
              >
                <FiRefreshCw className="h-3 w-3 mr-1" />
                Réessayer
              </Button>
            </div>

          ) : analysis ? (
            // État résultat - compact
            <div className="space-y-2">
              {/* Bandeau message (vert si déjà optimisé, neutre sinon) */}
              <Alert className={analysis.already_optimized ? 'border-green-200 bg-green-50' : getRecommendationBg(analysis.recommendation)}>
                {analysis.already_optimized && <FiCheckCircle className="h-3 w-3 text-green-600" />}
                <AlertDescription className="font-medium text-xs">
                  <strong>{analysis.message}</strong>
                </AlertDescription>
              </Alert>

              {/* Cards CU/MU/LU - toujours 3 cards (MU grisée si non applicable au domaine de tension) */}
              {(() => {
                    const couts = analysis.financial_analysis?.couts_par_version || {};
                    if (Object.keys(couts).length === 0) return null;
                    const cur = analysis.current_version;
                    const reco = analysis.recommended_version;
                    const labels = { CU: 'Courte (CU)', MU: 'Moyenne (MU)', LU: 'Longue (LU)' };
                    // On affiche TOUJOURS 3 cards. Si la version n'est pas dans
                    // couts_par_version (ex. MU pour BT_SUP_36/HTA), card grisée
                    // "Non applicable" — la grille tarifaire ENEDIS ne définit
                    // MU que pour BT_INF_36 (BTINFMU4).
                    const allKeys = ['CU', 'MU', 'LU'];
                    return (
                      <div className="grid gap-1.5 grid-cols-3">
                        {allKeys.map((key) => {
                          const value = couts[key];
                          const isAvailable = value !== undefined && value !== null;
                          const isCurrent = key === cur;
                          const isReco = key === reco;
                          const bg = !isAvailable
                            ? 'bg-gray-50 border border-dashed border-gray-300'
                            : isReco
                              ? 'bg-green-50 border border-green-300'
                              : isCurrent
                                ? 'bg-gray-100 border border-gray-300'
                                : 'bg-gray-50';
                          const txt = !isAvailable
                            ? 'text-gray-400'
                            : isReco ? 'text-green-700' : isCurrent ? 'text-gray-700' : 'text-gray-600';
                          const valTxt = !isAvailable
                            ? 'text-gray-400 italic'
                            : isReco ? 'text-green-900' : 'text-gray-900';
                          return (
                            <div key={key} className={`p-1.5 rounded text-center ${bg}`}>
                              <div className={`text-xs font-medium mb-1 ${txt}`}>
                                {labels[key] || key}
                                {isAvailable && isCurrent && <span className="ml-1 text-[10px] font-normal">(actuel)</span>}
                                {isAvailable && isReco && !isCurrent && <span className="ml-1 text-[10px] font-normal">★</span>}
                              </div>
                              <div className={`text-xs font-bold ${valTxt}`}>
                                {isAvailable ? formatCurrency(value) : 'Non applicable'}
                              </div>
                              <div className="text-[10px] text-gray-500">
                                {isAvailable ? 'par an' : 'pour ce domaine de tension'}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Impact économique - Compact */}
                  <div className={`p-1.5 rounded ${(analysis.financial_analysis?.economie_annuelle || 0) > 0 ? 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200' : 'bg-gradient-to-r from-red-50 to-pink-50 border border-red-200'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs font-medium text-gray-700">{(analysis.financial_analysis?.economie_annuelle || 0) > 0 ? 'Économie Annuelle' : 'Surcoût Annuel'}</div>
                        <div className={`text-xs font-bold flex items-center gap-2 ${(analysis.financial_analysis?.economie_annuelle || 0) > 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {(analysis.financial_analysis?.economie_annuelle || 0) > 0 ? <FiTrendingUp className="h-3.5 w-3.5" /> : <FiTrendingDown className="h-3.5 w-3.5" />}
                          {formatCurrency(Math.abs(analysis.financial_analysis?.economie_annuelle || 0))}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-medium text-gray-700">Impact</div>
                        <div className={`text-xs font-bold ${(analysis.financial_analysis?.economie_annuelle || 0) > 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {(analysis.financial_analysis?.economie_annuelle || 0) > 0 ? '+' : ''}{(analysis.financial_analysis?.pourcentage_economie || 0).toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Prochaines étapes - Compact */}
                  {analysis.financial_analysis?.rentable && analysis.next_steps && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-800 text-xs">Prochaines Étapes</h4>
                      <div className="space-y-2">
                        {analysis.next_steps.map((step, index) => (
                          <div key={index} className="flex items-start gap-2 p-2 bg-blue-50 rounded">
                            <div className="bg-blue-600 text-white text-xs font-bold rounded-full h-3 w-3 flex items-center justify-center flex-shrink-0 mt-0.5">{index + 1}</div>
                            <p className="text-xs text-blue-800">{step}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
              {/* Boutons d'action - Compact */}
              <div className="flex flex-wrap items-center gap-2 pt-3 border-t">
                <Button onClick={analyzeVersionUtilisation} variant="outline" size="sm" className="flex items-center gap-1">
                  <FiRefreshCw className="h-3 w-3" />
                  Actualiser
                </Button>
                {(() => {
                  const recommended = analysis.recommended_version;
                  const alreadyChosen = chosenVersionOfUse && chosenVersionOfUse === recommended;
                  if (!analysis.financial_analysis?.rentable) return null;
                  if (alreadyChosen) {
                    return (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium bg-emerald-100 text-emerald-700 border border-emerald-300">
                        <FiCheckCircle className="h-3 w-3" />
                        Version {recommended} validée optimisée
                      </span>
                    );
                  }
                  return (
                    <Button
                      onClick={handleConfirmOptimization}
                      disabled={confirming}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      size="sm"
                    >
                      {confirming ? 'Validation…' : `Procéder à l'optimisation (${recommended})`}
                    </Button>
                  );
                })()}
              </div>

              {/* Footer info - Compact */}
              <div className="text-xs text-gray-500 pt-3 border-t">
                Analyse basée sur {analysis.details?.periode_analyse}. 
                {analysis.details?.date_analyse && ` MAJ : ${new Date(analysis.details.date_analyse).toLocaleDateString('fr-FR')}`}
              </div>
            </div>
          ) : (
            // État initial - Compact
            <div className="text-center py-6">
              <FiInfo className="h-3 w-3 mx-auto mb-2 text-gray-400" />
              <p className="text-gray-600 mb-1.5 text-xs">
                Lancez l'analyse comparative CU / MU / LU
              </p>
              <Button 
                onClick={analyzeVersionUtilisation}
                className="bg-purple-600 hover:bg-purple-700 text-xs"
              >
                <FiPlay className="mr-2 h-3 w-3" />
                Lancer l'analyse
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VersionUtilisationOptimizer;